import { type FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { Select, type SelectOptionType } from "@onshopfront/components";
import LoadingScreen from "../../../app/Common/Loading/LoadingScreen";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../../Common/Button";
import CopyButton from "../../Common/CopyButton/CopyButton";
import Input from "../../Common/Input/Input";
import { useTitle } from "../../Utilities/TitleHook";

const LABEL_CLASS = "font-bold text-[var(--color-text-tertiary)] uppercase";

enum NotifyBy {
    EMAIL = "email",
    SLACK = "slack",
    NONE = "none",
}

const NotifyByOptions = [
    {
        label: "Email",
        value: NotifyBy.EMAIL,
    },
    {
        label: "Slack",
        value: NotifyBy.SLACK,
    },
    {
        label: "Don't notify me",
        value: NotifyBy.NONE,
    },
];

export const ApplicationEdit: FC = () => {
    useTitle("Application Modify");

    const [ name, setName ] = useState("");
    const [ redirectUri, setRedirectUri ] = useState("");
    const [ embedded, setEmbedded ] = useState("");
    const [ setupUri, setSetupUri ] = useState("");
    const [ clientSecret, setClientSecret ] = useState<string>("");
    const [ oldSecret, setOldSecret ] = useState<string | null>(null);
    const [ oldSecretExpiry, setOldSecretExpiry ] = useState<string | null>(null);
    const [ notifyBy, setNotifyBy ] = useState(NotifyBy.NONE);
    const [ notificationConfig, setNotificationConfig ] = useState("");
    const [ error, setError ] = useState<string | null>(null);
    const [ saving, setSaving ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ success, setSuccess ] = useState<string | null>(null);
    const [ approved, setApproved ] = useState(false);
    const [ canEmbed, setCanEmbed ] = useState(false);
    const [ marketplace, setMarketplace ] = useState({
        logo            : null,
        homepage        : null,
        support         : null,
        privacy         : null,
        shortDescription: null,
        longDescription : null,
    });

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);

        fetch(`/application/${id}`, {
            method : "GET",
            headers: {
                Accept        : "application/json",
                "Content-Type": "application/json",
            },
        })
            .then(async response => {
                if(!response.ok) {
                    throw await response.json();
                }

                return response.json();
            })
            .then(body => {
                const client = body.client;
                const notification = body.notification;

                setName(client.name);
                setRedirectUri(client.redirect);
                setEmbedded(client.embedded);
                setClientSecret(client.secret);
                setOldSecret(client.old_secret);
                setOldSecretExpiry(client.old_secret_expires_at);
                setApproved(!!client.approved);
                setCanEmbed(body.canEmbed);
                setSetupUri(client.setup_url ?? "");
                setMarketplace({
                    logo            : client.logo,
                    homepage        : client.homepage_url,
                    support         : client.support_url,
                    privacy         : client.privacy_url,
                    shortDescription: client.short_description,
                    longDescription : client.long_description,
                });

                if(notification) {
                    setNotifyBy(notification.method);
                    setNotificationConfig(notification.config);
                }
            })
            .catch((err) => {
                console.error("Failed to retrieve application details", err);
                setError(`Unable to retrieve application details: ${err.message || "An unknown error occurred"}`);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [ id ]);

    const resetSecret = () => {
        if(oldSecretExpiry) {
            setError("Client Secret cannot be reset until the old secret has expired");

            return;
        }

        fetch(`/application/${id}/reset-secret`, {
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
        })
            .then((response) => {
                if(response.status !== 200) {
                    throw new Error("Invalid Response");
                }

                return response.json();
            })
            .then(result => {
                setClientSecret(result.secret);
                setOldSecret(result.old_secret);
                setOldSecretExpiry(result.old_secret_expires_at);
                setSuccess("Successfully Reset Client Secret");

                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            })
            .catch((err) => {
                console.error("Developer API: Failed to reset Client Secret", err);
                setError(err instanceof Error ? err.message : JSON.stringify(err));
            });
    };

    const clearOldSecret = () => {
        if(!oldSecret) {
            setError("No old secret exists to clear");

            return;
        }

        fetch(`/application/${id}/clear-secret`, {
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
        })
            .then((response) => {
                if(response.status !== 204) {
                    throw new Error("Invalid Response");
                }
            })
            .then(() => {
                setOldSecret(null);
                setOldSecretExpiry(null);
                setSuccess("Successfully Cleared Old Secret");

                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            })
            .catch((err) => {
                console.error("Developer API: Failed to clear old Client Secret", err);
                setError(err instanceof Error ? err.message : JSON.stringify(err));
            });
    };

    const onNotifyChange = (e: SelectOptionType<NotifyBy>): void => {
        if(e && !Array.isArray(e)) {
            setNotifyBy(e.value);
            setNotificationConfig("");
        }
    };

    const onCancel = (): void => {
        navigate("/applications");
    };

    const onConfirm = (): void => {
        if(saving) {
            return;
        }

        setError(null);

        if(!name) {
            setError("Invalid Name");

            return;
        }

        if(!redirectUri) {
            setError("Invalid Redirect URI");

            return;
        }

        setSaving(true);

        fetch(`/application/${id}`, {
            method     : "PUT",
            credentials: "same-origin",
            headers    : {
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                name       : name,
                redirectUri: redirectUri,
                embedded   : embedded,
                setupUri   : setupUri,
                webhook    : notifyBy !== NotifyBy.NONE ? {
                    method: notifyBy,
                    config: notificationConfig,
                } : null,
            }),
        })
            .then(async response => {
                if(!response.ok) {
                    throw await response.json();
                }

                return;
            })
            .then(() => {
                setSuccess("Successfully Updated Application");

                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            })
            .catch(err => {
                console.error("Could not update the application", err);

                if(typeof err === "object" && err !== null && "message" in err) {
                    setError(err.message);
                } else {
                    setError(err instanceof Error ? err.message : JSON.stringify(err));
                }
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const formatSecret = (secret: string): string => {
        return secret.slice(-4).padStart(40, "x");
    };

    const notifyEmailIconLabel = [ "envelope", "Email Address" ];
    const notifySlackIconLabel = [ "globe", "URL" ];

    const notifySelectValue = NotifyByOptions.find((option) => option.value === notifyBy) ?? null;

    if(loading) {
        return <LoadingScreen />;
    }

    const [
        notifyInputIcon,
        notifyInputLabel,
    ] = notifyBy === NotifyBy.EMAIL ? notifyEmailIconLabel : notifySlackIconLabel;

    return (
        <div className="mt-[50px] p-4 max-w-[theme(screens.lg)] mx-auto">
            {success && (
                <div
                    className={
                        "rounded-lg shadow-md p-4 mb-2 bg-[var(--pill-green-bg)] text-[var(--pill-green-fg)]"
                    }
                >
                    {success}
                </div>
            )}
            {error && (
                <div
                    className={
                        "rounded-lg shadow-md p-4 mb-2 bg-[var(--pill-red-bg)] text-[var(--pill-red-fg)]"
                    }
                >
                    {error}
                </div>
            )}
            <div className="md:flex md:justify-around md:gap-8">
                <div className="flex-1">
                    <Input
                        icon="tag"
                        label="Name"
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                    />
                    <Input
                        icon="link"
                        label="Redirect URI"
                        type="text"
                        name="redirectUri"
                        value={redirectUri}
                        onChange={(e) => setRedirectUri(e.currentTarget.value)}
                    />
                    {(embedded !== "" || canEmbed) && (
                        <Input
                            icon="anchor"
                            label="Embedded API URI"
                            type="text"
                            name="embedded"
                            value={embedded}
                            onChange={(e) => setEmbedded(e.currentTarget.value)}
                        />
                    )}
                    {approved && (
                        <Input
                            icon="house"
                            label="Marketplace Direct Authentication URI"
                            type="text"
                            name="setupUri"
                            value={setupUri}
                            onChange={(e) => setSetupUri(e.currentTarget.value)}
                        />
                    )}
                    <div className="flex">
                        <div className="flex-1">
                            <Input
                                icon="key"
                                label="Client Secret"
                                type="text"
                                name="secret"
                                value={formatSecret(clientSecret)}
                                disabled={true}
                            />
                        </div>
                        <div className="my-auto text-xl">
                            <CopyButton text={clientSecret} />
                        </div>
                    </div>
                    {(oldSecret && oldSecretExpiry) && (
                        <>
                            <Input
                                icon="key"
                                label="Old Secret"
                                type="text"
                                name="oldSecret"
                                value={formatSecret(oldSecret)}
                                disabled={true}
                            />
                            <Input
                                icon="clock"
                                label="Old Secret Expires At"
                                type="text"
                                name="oldSecretExpiry"
                                value={oldSecretExpiry}
                                disabled={true}
                            />
                        </>
                    )}
                    <div className="mt-4">
                        Get notified when your webhooks fail
                        <Select
                            options={NotifyByOptions}
                            value={notifySelectValue}
                            onChange={onNotifyChange}
                            className="w-full"
                        />
                        {notifyBy !== NotifyBy.NONE && (
                            <Input
                                icon={notifyInputIcon as IconProp}
                                label={notifyInputLabel}
                                type="text"
                                name="notifyAt"
                                value={notificationConfig}
                                onChange={(e) => setNotificationConfig(e.currentTarget.value)}
                            />
                        )}
                        {notifyBy === NotifyBy.SLACK && (
                            <div
                                className={
                                    "mt-2 p-1 text-center rounded bg-[var(--pill-amber-bg)] " +
                                    "text-[var(--pill-amber-fg)]"
                                }
                            >
                                <p>
                                    To receive Slack notifications, you&apos;ll need to install the{" "}
                                    <a href="https://slack.com/apps/A0F7XDUAZ-incoming-webhooks">
                                        Incoming Webhooks
                                    </a>
                                    {" "}Slack app
                                </p>
                                <p>Once installed and configured, enter the assigned URL above</p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1">
                    {approved && (
                        <>
                            <div className="my-4 text-[var(--pill-green-fg)] text-center">
                                <FontAwesomeLight icon="check-circle" /> Approved
                            </div>
                            <div className="prose mb-4">
                                <p>
                                    This application has been approved by Shopfront. You&apos;re able to integrate
                                    your application with any store on any plan.
                                </p>
                                <p>
                                    You also have priority developer support. If you need assistance, feel free to
                                    email us on{" "}
                                    <a href="mailto:developers@shopfront.com.au">developers@shopfront.com.au</a>
                                </p>
                                <p>
                                    If you&apos;d like to adjust any of the below details, please get in touch with
                                    us using the above email address.
                                </p>
                            </div>
                            {marketplace.logo !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Logo</span>
                                    <div>
                                        <img
                                            src={marketplace.logo}
                                            alt={`${name} Logo`}
                                            className="max-w-full h-auto md:max-w-sm lg:max-w-lg"
                                        />
                                    </div>
                                </div>
                            )}
                            {marketplace.shortDescription !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Short Description</span>
                                    <div>
                                        <p>{marketplace.shortDescription}</p>
                                    </div>
                                </div>
                            )}
                            {marketplace.longDescription !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Long Description</span>
                                    <div className="whitespace-pre-wrap">
                                        {marketplace.longDescription}
                                    </div>
                                </div>
                            )}
                            {marketplace.homepage !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Homepage</span>
                                    <div>{marketplace.homepage}</div>
                                </div>
                            )}
                            {marketplace.privacy !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Privacy Policy</span>
                                    <div>{marketplace.privacy}</div>
                                </div>
                            )}
                            {marketplace.support !== null && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Support</span>
                                    <div>{marketplace.support}</div>
                                </div>
                            )}
                            {setupUri !== "" && (
                                <div className="mb-4">
                                    <span className={LABEL_CLASS}>Setup</span>
                                    <div>{setupUri}</div>
                                </div>
                            )}
                        </>
                    )}
                    {!approved && (
                        <>
                            <div className="my-4 text-[var(--pill-red-fg)] text-center">
                                <FontAwesomeLight icon="times-circle" /> Not Approved
                            </div>
                            <div className="prose">
                                <p>
                                    This application hasn&apos;t been approved by Shopfront. You&apos;re still able
                                    to integrate your application with stores that are running on the Premium plan.
                                </p>
                                <p>
                                    If you&apos;re interested in finding out more about the partner program,{" "}
                                    <Link to="/partner">click here</Link>.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="dialog-buttons">
                <Button
                    type="button"
                    classes={[ "normal", "mr-auto!" ]}
                    onClick={oldSecret ? clearOldSecret : resetSecret}
                >
                    {oldSecret ? "Clear Old Secret" : "Reset Secret"}
                </Button>
                <Button
                    type="button"
                    classes={[ "secondary" ]}
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "primary" ]}
                    onClick={onConfirm}
                    disabled={saving}
                >
                    {saving ? "Saving..." : "Confirm"}
                </Button>
            </div>
        </div>
    );
};
